import { m } from "@plannello/i18n/messages";
import type { QueryWarning } from "@plannello/shared";
import { type ReactNode, useMemo } from "react";
import { ProductFallback, ProductHeader, useProductPage } from "@/components/planning/ProductHeader";
import { TimelineView } from "@/components/planning/timeline/TimelineView";
import { useBreadcrumbs } from "@/components/shell/breadcrumbs";
import { useRunCommand } from "@/components/shell/useRunCommand";
import { Button, EmptyState, Kbd, KeyHint } from "@/components/ui";
import { DisplayOptions } from "@/components/views/DisplayOptions";
import { SaveViewHost } from "@/components/views/SaveViewDialog";
import { useLoadedView, ViewControls } from "@/components/views/ViewControls";
import { ViewsMenu } from "@/components/views/ViewsMenu";
import { viewName } from "@/components/views/viewMeta";
import { FilterBar } from "./FilterBar";
import { ItemsView } from "./ItemsView";
import { useReadOnly } from "./readOnly";
import { LayoutSwitch } from "./ViewToolbar";
import { useSetViewSearch, type ViewSearch } from "./viewSearch";

export const lastLayoutKey = (productKey: string) => `pt-layout-${productKey}`;

export type ProductLayout = "list" | "board" | "timeline";

const layoutLabel = (l: ProductLayout) =>
  ({ list: m.layout_list(), board: m.layout_board(), timeline: m.layout_timeline() })[l];

/**
 * A product's items as a list, board or timeline: the product header (sections, views menu,
 * layout switch, display options), the filter bar with the saved-view state next to it, and the
 * items. Everything that defines the view lives in the URL.
 */
export function ProductView({
  productKey,
  layout,
  search,
}: {
  productKey: string;
  layout: ProductLayout;
  search: ViewSearch;
}) {
  const { product, space, name, isPending } = useProductPage(productKey);
  const setSearch = useSetViewSearch();
  const run = useRunCommand();
  const readOnly = useReadOnly();
  const productIds = useMemo(() => (product ? [product.id] : []), [product]);
  const loaded = useLoadedView(productKey.toUpperCase(), search.view);

  useBreadcrumbs(
    product && space
      ? [
          { label: space.name, to: `/s/${space.key}` },
          { label: name, to: `/p/${product.key}` },
          { label: loaded ? viewName(loaded) : layoutLabel(layout) },
        ]
      : [{ label: productKey }],
  );

  try {
    localStorage.setItem(lastLayoutKey(productKey.toUpperCase()), layout);
  } catch {
    // private mode
  }

  if (!product) return <ProductFallback isPending={isPending} />;

  const header = (r: { count: number; warnings: QueryWarning[]; isSearching: boolean }): ReactNode => (
    <ProductHeader
      product={product}
      space={space}
      name={name}
      section="items"
      actions={
        <>
          <ViewsMenu product={product} currentId={search.view} />
          <LayoutSwitch productKey={product.key} current={layout} search={search} />
          <DisplayOptions layout={layout} search={search} />
        </>
      }
      toolbar={
        <div className="flex min-w-0 flex-wrap items-start gap-x-3 gap-y-2">
          <div className="min-w-0 flex-1 basis-72">
            <FilterBar
              value={search.q ?? ""}
              onChange={(q) => setSearch({ q })}
              warnings={r.warnings}
              isSearching={r.isSearching}
              productIds={productIds}
              onExit={() =>
                document
                  .querySelector<HTMLElement>(
                    '[role="treegrid"] [tabindex="0"], [role="grid"] [tabindex="0"], [data-timeline-bar]',
                  )
                  ?.focus()
              }
            />
          </div>
          <div className="flex min-h-(--input-h) shrink-0 items-center gap-2">
            <ViewControls product={product} layout={layout} search={search} />
            <span className="text-xs text-fg-subtle tnum" aria-live="polite">
              {r.count === 1 ? m.list_count_one() : m.list_count({ count: r.count })}
            </span>
          </div>
        </div>
      }
    />
  );

  return (
    <div className="flex h-full min-h-0 flex-col">
      {layout === "timeline" ? (
        <TimelineView product={product} search={search} header={header} />
      ) : (
        <ItemsView
          productIds={productIds}
          layout={layout}
          search={search}
          defaultGroup="status"
          label={`${name}, ${layoutLabel(layout)}`}
          viewKey={`p:${product.key}:${layout}`}
          createDefaults={() => ({ productId: product.id })}
          empty={
            <EmptyState
              title={m.product_empty_title({ product: name })}
              description={m.product_empty_body()}
              action={
                readOnly ? undefined : (
                  <Button variant="primary" onPress={() => run("create")}>
                    {m.action_create_item()}
                    <Kbd keys={["C"]} tone="inverse" className="-mr-1 ml-1" />
                  </Button>
                )
              }
              secondary={
                readOnly ? undefined : (
                  <Button variant="secondary" onPress={() => run("import.github")}>
                    {m.import_github()}
                  </Button>
                )
              }
              hint={<KeyHint keys={["mod", "K"]}>{m.hint_command_palette()}</KeyHint>}
            />
          }
        >
          {(r) => header({ count: r.items.length, warnings: r.warnings, isSearching: r.isSearching })}
        </ItemsView>
      )}
      <SaveViewHost product={product} layout={layout} search={search} />
    </div>
  );
}
